import {assert, assertAndContinue} from 'Asserts';
import {BulkAntibody} from 'bulk-workflows/antibody/BulkAntibodyConstants';
import {EventConstants} from 'constants/EventConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderDAO from 'file-browser/FolderDAO';
import AppDispatcher from 'lib/Dispatcher';
import * as FullScreenActions from 'nav/full-screen/FullScreenActions';
import * as ToastActions from 'nav/toasts/ToastActions';
import {ToastType} from 'nav/toasts/ToastConstants';
import notifications from 'notifications';
import * as SchemaFieldConstants from 'schemas/fields/SchemaFieldConstants';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import getTagSchemaFromCache from 'schemas/util/getTagSchemaFromCache';

import {TagSchemaListingWithFieldsGraphQL} from '../../../graphql-fragments/schemas/__generated/TagSchemaListingWithFieldsGraphQL';
import * as BulkAntibodyDAO from './BulkAntibodyDAO';
import BulkAntibodyStore from './BulkAntibodyStore';
import * as BulkAntibodyUtil from './BulkAntibodyUtil';

function _getTagSchema(tagSchemaId: string): TagSchemaListingWithFieldsGraphQL {
  const tagSchema = getTagSchemaFromCache(tagSchemaId);
  assert(tagSchema != null, 'Tag schema in bulk antibody was not found in cache');
  return tagSchema;
}

function _setAntibodyNamePrefixOnUpdate(bulkAntibodyUpdates: Partial<BulkAntibody>): void {
  if (!bulkAntibodyUpdates.tagSchemaId) {
    return;
  }
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ANTIBODY_UPDATE,
    updates: {antibodyNamePrefix: _getTagSchema(bulkAntibodyUpdates.tagSchemaId).prefix},
  });
}

/**
 * Set bioentity link specifications to the user's selected `tagSchemaId`'s bioentity link fields with
 * matching bioentity values from the `inputFiles` if either input parameter changes. For example, if the
 * user switches to "Antibody" tag schema ("Heavy Chain" field requiring "Chain" schema), then this creates
 * the "Heavy Chain" translation specification with matching "Chain" files.
 */
function _setBioentityLinkSpecsOnUpdate(bulkAntibodyUpdates: Partial<BulkAntibody>): void {
  if (!bulkAntibodyUpdates.tagSchemaId && !bulkAntibodyUpdates.inputFiles) {
    return;
  }
  const bulkAntibody = BulkAntibodyUtil.Update.update(
    BulkAntibodyStore.getBulkAntibody(),
    bulkAntibodyUpdates
  );
  if (!bulkAntibody.tagSchemaId) {
    return;
  }
  const bioentityLinkFields = _getTagSchema(bulkAntibody.tagSchemaId).fields.filter(
    (field: SchemaField) =>
      field.fieldType === SchemaFieldConstants.FIELD_TYPES.BIOENTITY_LINK.type &&
      field.requiredLink != null &&
      'tagSchema' in field.requiredLink &&
      field.requiredLink.tagSchema != null
  );
  const bioentityLinkSpecs = bioentityLinkFields.map((tagSchemaField: SchemaField) =>
    BulkAntibodyUtil.createBioentityLinkSpec(tagSchemaField, bulkAntibody.inputFiles)
  );
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ANTIBODY_UPDATE,
    updates: {bioentityLinkSpecs},
  });
}

export function update(updates: Partial<BulkAntibody>): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ANTIBODY_UPDATE,
    updates,
  });
  _setAntibodyNamePrefixOnUpdate(updates);
  _setBioentityLinkSpecsOnUpdate(updates);
}

/**
 * Convert the input folder to all of its files.
 *
 * NOTE: This is temporary until `MultipleFilesSelector` is fully implemented.
 */
export function fetchInputFiles(folderId: string): void {
  const LARGE_LIMIT = 1000;

  update({inputFilesLoading: true});
  FolderDAO.listFiles(folderId, {sort: {limit: LARGE_LIMIT}})
    .done((response) => {
      assertAndContinue(
        response.total <= LARGE_LIMIT,
        `Bulk antibody input folder has ${response.total} files, we only fetched ${LARGE_LIMIT}`
      );
      update({inputFiles: response.files});
    })
    .fail(notifications.notifyError)
    .always(() => {
      update({inputFilesLoading: false});
    });
}

export function create(): void {
  const bulkAntibody = BulkAntibodyStore.getBulkAntibody();
  assert(!bulkAntibody.inputFilesLoading, 'Expected files in bulk antibody to already be loaded');
  assert(!bulkAntibody.isCreating, 'Expected bulk antibody to not already be creating');
  AppDispatcher.dispatch({
    actionType: EventConstants.BULK_ANTIBODY_UPDATE,
    updates: {isCreating: true},
  });
  BulkAntibodyDAO.create(bulkAntibody)
    .done(() => {
      // Completed
      ToastActions.pushToast({
        type: ToastType.INFO,
        message: 'Bulk antibody creation completed.',
        actionText: 'Open',
        duration: 0,
        onActionClick() {
          FullScreenActions.hideScreen();
          FileBrowserActions.openFile(bulkAntibody.outputFolderId!);
        },
      });
    })
    .fail(notifications.notifyError)
    .always(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.BULK_ANTIBODY_UPDATE,
        updates: {isCreating: false},
      });
    });
}

export function reset(): void {
  update(BulkAntibodyUtil.createBulkAntibody());
}
